import { gql } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { jsonToGraphQLQuery, VariableType } from 'json-to-graphql-query';
import type { FieldValue } from './types';

interface TimecodeMetadataBaseResult {
  duration: FieldValue;
  durationSeconds: FieldValue;
  timecodeOffsetSeconds: FieldValue;
  timecodeOffset: FieldValue;
  framerate: FieldValue;
  dropframe: FieldValue;
  frameDelimiter: FieldValue;
  inTimecode: FieldValue;
  outTimecode: FieldValue;
  inTimecodeSeconds: FieldValue;
  outTimecodeSeconds: FieldValue;
  finalDelimiter: FieldValue;
}

interface AssetTimecodeMetaQueryResult {
  query: {
    __variables: {
      assetId: 'ID';
    };
    assetTimecodeMetadata: TimecodeMetadataBaseResult & {
      __args: {
        assetId: VariableType;
      };
    };
  };
}

interface TimeCodeFromSecondsQueryResult {
  query: {
    timecodeFromSeconds: {
      __args: {
        seconds: number;
        timeCode: string;
        fps: string;
      };
      timecode: FieldValue;
    };
  };
}

interface TimeCodeFromSecondsAssetInQueryResult {
  query: {
    __variables: {
      assetId: 'ID!';
      seconds: 'Float!';
    };
    timecodeFromSecondsAssetIn: {
      __args: {
        assetId: VariableType;
        seconds: VariableType;
      };
      timecode: FieldValue;
    };
  };
}

interface MarkerTimecodeMetaQueryResult {
  query: {
    __variables: {
      markerId: 'ID';
    };
    markerTimecodeMetadata: TimecodeMetadataBaseResult & {
      __args: {
        markerId: VariableType;
      };
    };
  };
}

export const timecodeMetadataBase = (truify: boolean = false): TimecodeMetadataBaseResult => ({
  duration: true,
  durationSeconds: true,
  timecodeOffsetSeconds: true,
  timecodeOffset: true,
  framerate: true,
  dropframe: true,
  frameDelimiter: true,
  inTimecode: truify,
  outTimecode: truify,
  inTimecodeSeconds: truify,
  outTimecodeSeconds: truify,
  finalDelimiter: truify,
});

export const assetTimecodeMeta = (truify: boolean = false): AssetTimecodeMetaQueryResult => ({
  query: {
    __variables: {
      assetId: 'ID',
    },
    assetTimecodeMetadata: {
      __args: {
        assetId: new VariableType('assetId'),
      },
      ...timecodeMetadataBase(truify),
    },
  },
});

type AssetTimecodeMetaSelectorFn = (meta: TimecodeMetadataBaseResult) => void;

export const buildAssetTimecodeMetaQuery = (
  truify: boolean = false,
  customize?: AssetTimecodeMetaSelectorFn,
): DocumentNode => {
  const atm = assetTimecodeMeta(truify);
  customize?.(atm.query.assetTimecodeMetadata);
  return gql(jsonToGraphQLQuery(atm, { pretty: true }));
};

export const timeCodeFromSeconds = (truify: boolean = true): TimeCodeFromSecondsQueryResult => ({
  query: {
    timecodeFromSeconds: {
      __args: {
        seconds: 0,
        timeCode: '00:00:00:00',
        fps: '30',
      },
      timecode: truify,
    },
  },
});

export const timeCodeFromSecondsAssetIn = (truify: boolean = true): TimeCodeFromSecondsAssetInQueryResult => ({
  query: {
    __variables: {
      assetId: 'ID!',
      seconds: 'Float!',
    },
    timecodeFromSecondsAssetIn: {
      __args: {
        assetId: new VariableType('assetId'),
        seconds: new VariableType('seconds'),
      },
      timecode: truify,
    },
  },
});

export const markerTimecodeMeta = (truify: boolean = true): MarkerTimecodeMetaQueryResult => ({
  query: {
    __variables: {
      markerId: 'ID',
    },
    markerTimecodeMetadata: {
      __args: {
        markerId: new VariableType('markerId'),
      },
      ...timecodeMetadataBase(truify),
    },
  },
});
